import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";

import { Button, Card, FormGroup, Input, PageHeader } from "@cofano/cck";
import { Alert, Grid2 as Grid } from "@mui/material";
import { ArrowDownward } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import DatePicker from "components/cck/DatePicker";
import Helmet from "components/helmet/Helmet";
import { request } from "components/utils/Request2";
import { TableId } from "components/table";

import { RelationLink } from "utils/LinkUtils";
import { DataFormat, Language, hasPluginPermission, usePermission } from "imports/Utils";

import AdvancePaymentDialog from "view/invoices/components/dialogs/AdvancePaymentDialog";
import DeclarationsTable, { type DeclarationRow } from "view/declarations/components/table/DeclarationsTable";
import PaymentTermSelect from "view/paymentterms/components/selects/PaymentTermSelect";
import PreviousInvoicesTable from "view/invoices/components/table/PreviousInvoicesTable";
import StagedDeclarationsTable from "view/invoices/components/table/StagedDeclarationsTable";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import useOpenCharterSideviewer from "view/charters/components/sideviewer/useOpenCharterSideviewer";

import { invoiceCreateSchema, InvoiceCreateSchemaInput, InvoiceCreateSchemaOutput } from "./utils/invoice.schema";

import { InvoiceableRelationsDocument } from "__generated__/view/invoices/graphql/invoices.queries.generated";
import { CharterType } from "__generated__/enums";

const InvoiceCreate = ({ charterType }: { charterType: CharterType }) => {

	const navigate = useNavigate();
	const session = useStoreState(store => store.session);
	const { isReadOnly } = usePermission();

	const openCharterSideviewer = useOpenCharterSideviewer();

	const [ selectedDeclarations, setSelectedDeclarations ] = useState<DeclarationRow[]>([]);
	const [ submitting, setSubmitting ] = useState(false);

	const [ advanceDialogCharter, setAdvanceDialogCharter ] = useState<DeclarationRow["charter"] | null>(null);
	const [ declarationsReloadToken, setDeclarationsReloadToken ] = useState(0);

	const stageDeclaration = (row: DeclarationRow) =>
		setSelectedDeclarations(prev => prev.some(d => d.id === row.id) ? prev : [ ...prev, row ]);

	const unstageDeclaration = (row: DeclarationRow) =>
		setSelectedDeclarations(prev => prev.filter(d => d.id !== row.id));

	const { control, watch, setValue, handleSubmit } = useForm<InvoiceCreateSchemaInput, unknown, InvoiceCreateSchemaOutput>({
		resolver: zodResolver(invoiceCreateSchema),
		defaultValues: {
			invoiceNumber: "",
			creditor: null,
			debtor: null,
			date: null,
			paymentTerm: null,
			lines: [],
		},
	});

	const creditor = watch("creditor");
	const debtor = watch("debtor");

	const { data: invoiceableData } = useQuery(InvoiceableRelationsDocument);
	const invoiceableRelations = invoiceableData?.searchInvoicelines?.content ?? [];

	const uniqueCreditors = Array.from(
		new Map(invoiceableRelations.flatMap(l => l?.creditor ? [ [ l.creditor.id, l.creditor ] as const ] : [])).values(),
	);

	const uniqueDebtors = Array.from(
		new Map(invoiceableRelations.flatMap(l => l?.debtor ? [ [ l.debtor.id, l.debtor ] as const ] : [])).values(),
	);

	useEffect(() => {
		if (uniqueCreditors.length === 1 && !creditor) {
			setValue("creditor", uniqueCreditors[0]);
		}
		if (uniqueDebtors.length === 1 && !debtor) {
			setValue("debtor", uniqueDebtors[0]);
		}
	}, [ uniqueCreditors.length, uniqueDebtors.length ]);

	const stagedLines = selectedDeclarations
		.flatMap(d => d?.invoiceLines ?? [])
		.filter((l): l is NonNullable<typeof l> => l != null);
	const stagedCreditors = Array.from(
		new Map(stagedLines.flatMap(l => l.creditor ? [ [ l.creditor.id, l.creditor ] as const ] : [])).values(),
	);
	const stagedDebtors = Array.from(
		new Map(stagedLines.flatMap(l => l.debtor ? [ [ l.debtor.id, l.debtor ] as const ] : [])).values(),
	);

	const filterOverride = [
		...(stagedCreditors.length > 0 ? [ { property: "creditor", value: stagedCreditors } ] : []),
		...(stagedDebtors.length > 0 ? [ { property: "debtor", value: stagedDebtors } ] : []),
	];

	const hasMixedRelations = stagedCreditors.length > 1 || stagedDebtors.length > 1;

	const newInvoiceTotal = selectedDeclarations.reduce((sum, d) => sum + (d.totalPriceExcVAT ?? 0), 0);

	useEffect(() => {
		if (stagedCreditors.length === 1) {
			setValue("creditor", stagedCreditors[0]);
		}
		if (stagedDebtors.length === 1) {
			setValue("debtor", stagedDebtors[0]);
		}
		setValue("lines", stagedLines);
	}, [ selectedDeclarations ]);

	const createInvoice = handleSubmit((form) => {
		const sourceLines = selectedDeclarations.flatMap(d => d?.invoiceLines ?? []);
		setSubmitting(true);
		const allLines = sourceLines.map(l => ({
			...l,
			charter: undefined,
			creditor: l?.creditor ?? form.creditor,
			debtor: l?.debtor ?? form.debtor,
		}));
		request<{ id: number }>({
			method: "POST",
			url: "invoices",
			body: { ...form, lines: allLines },
		}).then((response) => {
			navigate(`/view/invoices/${response.id}/details`);
		}).finally(() => setSubmitting(false));
	});

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("ADMINISTRATION_INVOICING")}
					<Helmet title={intl.formatMessage({ id: "ADMINISTRATION_INVOICING" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("..")} variant="outlined">
						{Language("INVOICES")}
					</Button>

					{!isReadOnly && (
						<Button
							onClick={createInvoice}
							loading={submitting}
							disabled={selectedDeclarations.length === 0 || hasMixedRelations}>
							{Language("SAVE")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Grid container spacing={2}>
				<Grid size={3}>
					<Grid size={12} marginBottom={2}>
						<Card title={intl.formatMessage({ id: "DETAILS" })}>
							<FormGroup label={Language("INVOICE_NUMBER")} labelPlacement="left-center">
								<Controller
									name="invoiceNumber"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<Input {...field} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>

							<FormGroup label={Language("INVOICE_CREDITOR")} labelPlacement="left-center" required>
								<Controller
									name="creditor"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<RelationSelect {...field} search={{ invoiceLineCreditor: true }} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>

							<FormGroup label={Language("INVOICE_DEBTOR")} labelPlacement="left-center" required>
								<Controller
									name="debtor"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<RelationSelect {...field} search={{ invoiceLineDebtor: true }} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>

							<FormGroup label={Language("INVOICE_DATE")} labelPlacement="left-center" required>
								<Controller
									name="date"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<DatePicker {...field} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>

							{hasPluginPermission(session, "paymentTerms") && (
								<FormGroup label={Language("PAYMENT_TERM")} labelPlacement="left-center" required>
									<Controller
										name="paymentTerm"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<PaymentTermSelect {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							)}
						</Card>
					</Grid>

					{selectedDeclarations.length > 0 && (
						<Grid size={12} marginBottom={2}>
							<Card title={intl.formatMessage({ id: "INVOICE_TOTAL_AMOUNT" })}>
								<FormGroup label={Language("TOTAL_EXCLUDE_VAT")} labelPlacement="left-center">
									<span>{DataFormat.price(newInvoiceTotal)}</span>
								</FormGroup>
							</Card>
						</Grid>
					)}

					{creditor && debtor && (
						<Grid size={12}>
							<Card
								title={intl.formatMessage({ id: "PREVIOUS_INVOICES" })}
								subtitle={<RelationLink relation={debtor} />}
								disablePadding>
								<PreviousInvoicesTable creditor={creditor} debtor={debtor} />
							</Card>
						</Grid>
					)}
				</Grid>

				<Grid size={9} container>
					<Grid size={12}>
						{selectedDeclarations.length == 0 && (
							<Alert severity="warning" style={{ marginBottom: 16 }}>
								{intl.formatMessage({ id: "SELECT_DECLARATIONS_TO_CREATE_INVOICE" })}
							</Alert>
						)}

						<DeclarationsTable
							tableId={TableId.InvoiceDeclarations}
							stagedIds={selectedDeclarations.map(d => d.id)}
							filterOverride={filterOverride}
							forceInvoiced={false}
							charterType={charterType}
							reloadToken={declarationsReloadToken}
							rowActions={[ {
								icon: <PaidRoundedIcon />,
								tooltip: intl.formatMessage({ id: "NEW_ADVANCE_PAYMENT" }),
								onClick: (row) => setAdvanceDialogCharter(row.charter),
							}, {
								icon: <AddIcon />,
								tooltip: intl.formatMessage({ id: "ADD_TO_INVOICE" }),
								onClick: (row) => stageDeclaration(row),
							} ]}
							onRowClick={(row, refetch) => {
								if (row.charter) {
									openCharterSideviewer(row.charter.id, () => refetch());
								}
							}}
						/>
					</Grid>

					{selectedDeclarations.length > 0 && (
						<>
							<Grid size={12} display="flex" justifyContent="center">
								<ArrowDownward />
							</Grid>

							<Grid size={12}>
								<Card
									title={intl.formatMessage({ id: "SELECTED_DECLARATIONS" })}
									disablePadding
								>
									<StagedDeclarationsTable
										declarations={selectedDeclarations}
										onRemove={unstageDeclaration} />
								</Card>
							</Grid>
						</>
					)}
				</Grid>
			</Grid>

			{advanceDialogCharter != null && (
				<AdvancePaymentDialog
					open={true}
					onClose={() => setAdvanceDialogCharter(null)}
					charterId={advanceDialogCharter.id}
					currency={advanceDialogCharter.contract?.currency?.isoCode ?? "EUR"}
					currentTotal={newInvoiceTotal}
					onCreated={(declaration) => {
						stageDeclaration(declaration);
						setDeclarationsReloadToken(token => token + 1);
					}}
				/>
			)}
		</div>
	);
};

export default InvoiceCreate;
